import { NgTemplateOutlet } from "@angular/common"
import { CdkVirtualScrollViewport, ScrollingModule } from "@angular/cdk/scrolling"
import { Component, ElementRef, OnDestroy, TemplateRef, ViewChild, computed, effect, input, model, output, signal } from "@angular/core"
import { MatButtonModule } from "@angular/material/button"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatSelect, MatSelectModule } from "@angular/material/select"
import { SearchableSelectOption } from "../../models"

@Component({
    selector: "valben-searchable-select",
    standalone: true,
    imports: [
        NgTemplateOutlet,
        ScrollingModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSelectModule,
    ],
    templateUrl: "./searchable-select.component.html",
    styles: [`
        :host {
            display: inline-block;
        }
    `],
})
export class SearchableSelectComponent<T, TData = unknown> implements OnDestroy {
    options = input.required<readonly SearchableSelectOption<T, TData>[]>()
    /** Single-select value. Ignored when `multiple` is set. */
    value = model<T | null>(null)
    /** Multi-select values. Only used when `multiple` is set. */
    values = model<readonly T[]>([])
    multiple = input(false)
    placeholder = input("")
    searchPlaceholder = input("Traži...")
    noEntriesLabel = input("Nema rezultata")
    panelWidth = input<string | number | null>("auto")
    panelClass = input<string | string[]>("valben-searchable-select-panel")
    virtualScroll = input(false)
    virtualItemSize = input(48)
    virtualHeight = input(200)
    virtualMinBuffer = input(200)
    virtualMaxBuffer = input(400)
    loading = input(false)
    optionTemplate = input<TemplateRef<{ $implicit: SearchableSelectOption<T, TData> }> | null>(null)
    /**
     * Server-paged mode: the caller filters and pages. Typing emits `searchChange` (debounced) and scrolling
     * to the end emits `scrolledToEnd`; `options` is rendered as-is instead of being filtered here.
     */
    serverSide = input(false)
    searchDebounce = input(600)
    /** Max labels rendered in the multi-select trigger before it collapses to "+N". */
    maxSelectedLabels = input(10)
    /** Keep selected values first in a multi-select list. */
    pinSelected = input(false)
    /** Clear a single-select's temporary value after emitting it (used by add-one pickers). */
    clearAfterSelection = input(false)

    searchChange = output<string>()
    scrolledToEnd = output<void>()

    readonly search = signal("")
    readonly filteredOptions = computed(() => {
        const query = this.search().trim().toLocaleLowerCase()
        const visible = this.serverSide() || !query
            ? this.options()
            : this.options().filter(option =>
                (option.searchText ?? option.label).toLocaleLowerCase().includes(query)
            )

        if (!this.multiple() || !this.pinSelected() || this.values().length === 0) return visible

        const selectedValues = new Set(this.values())
        const visibleValues = new Set(visible.map(option => option.value))
        const pinned = this.values()
            .filter(value => visibleValues.has(value))
            .map(value => visible.find(option => Object.is(option.value, value)))
            .filter((option): option is SearchableSelectOption<T, TData> => !!option)

        return [...pinned, ...visible.filter(option => !selectedValues.has(option.value))]
    })
    readonly selectedOption = computed(() =>
        this.options().find(option => Object.is(option.value, this.value()))
    )
    readonly selectedOptions = computed(() => {
        const picked = this.values()
        return picked
            .map(value => this.options().find(option => Object.is(option.value, value)))
            .filter((option): option is SearchableSelectOption<T, TData> => !!option)
    })
    /**
     * Values selected but not in the currently rendered window. Virtual scroll only renders a slice, and
     * mat-select drops a selection whose <mat-option> disappears — so these get hidden options to hold onto.
     */
    readonly offscreenSelected = computed(() => {
        const picked = this.multiple() ? this.values() : this.value() === null ? [] : [this.value() as T]
        // A virtual viewport destroys options outside its rendered range. Persistent hidden options keep
        // Material's selection model stable while those visible rows are recycled.
        if (this.virtualScroll()) return picked

        const rendered = new Set<T>(this.filteredOptions().map(option => option.value))
        return picked.filter(value => !rendered.has(value))
    })

    @ViewChild("searchInput") private _searchInput?: ElementRef<HTMLInputElement>
    @ViewChild(CdkVirtualScrollViewport) private _viewport?: CdkVirtualScrollViewport
    @ViewChild(MatSelect) private _select?: MatSelect

    private _searchTimer: ReturnType<typeof setTimeout> | undefined
    private readonly _reresolveSelection = effect(() => {
        const expected = this.multiple() ? this.values().length : this.value() === null ? 0 : 1
        this.options()

        const select = this._select
        if (!select?.options) return
        const selected = select.selected
        const applied = Array.isArray(selected) ? selected.length : selected ? 1 : 0
        if (applied !== expected) select.options.notifyOnChanges()
    })

    readonly compareValues = (left: T | null, right: T | null): boolean => Object.is(left, right)
    readonly trackOption = (_index: number, option: SearchableSelectOption<T, TData>): T => option.value

    optionForValue(value: T): SearchableSelectOption<T, TData> | undefined {
        return this.options().find(option => Object.is(option.value, value))
    }

    get triggerLabels(): string {
        const selected = this.selectedOptions()
        const shown = selected.slice(0, this.maxSelectedLabels()).map(option => option.label).join(", ")
        const rest = selected.length - this.maxSelectedLabels()
        return rest > 0 ? `${shown} (+${rest})` : shown
    }

    select(value: T | T[]): void {
        if (this.multiple()) {
            const picked = value as T[]
            const added = picked.length > this.values().length
            this.values.set(picked)
            if (added && this.pinSelected()) {
                requestAnimationFrame(() => {
                    this._viewport?.scrollToIndex(0)
                    this._viewport?.checkViewportSize()
                })
            }
            return
        }
        this.value.set(value as T)
        if (this.clearAfterSelection()) {
            this.value.set(null)
            if (this._select) this._select.value = null
        }
    }

    updateSearch(event: Event): void {
        this.search.set((event.target as HTMLInputElement).value)
        this._viewport?.scrollToIndex(0)
        this._emitSearch()
    }

    clearSearch(): void {
        this.search.set("")
        this._viewport?.scrollToIndex(0)
        this._searchInput?.nativeElement.focus()
        this._emitSearch()
    }

    handleSearchKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape") {
            if (this.search()) {
                event.preventDefault()
                event.stopPropagation()
                this.clearSearch()
            }
            return
        }

        if (event.key === "Tab") return
        event.stopPropagation()
    }

    handleOpenedChange(open: boolean): void {
        if (!open) {
            clearTimeout(this._searchTimer)
            this.search.set("")
            return
        }

        // A virtual scroller with zero items never scrolls, so in server mode nothing would ever ask for the
        // first window. Kick it off when the panel opens.
        if (this.serverSide()) this.searchChange.emit("")

        setTimeout(() => {
            if (this._select?.panel) this._select.panel.nativeElement.scrollTop = 0
            this._viewport?.scrollToIndex(0)
            this._viewport?.checkViewportSize()
            this._searchInput?.nativeElement.focus()
        })
    }

    handleScrolledIndexChange(): void {
        if (!this.serverSide() || this.loading()) return

        const range = this._viewport?.getRenderedRange()
        const total = this.options().length
        if (!range || total === 0) return
        if (range.end >= total) this.scrolledToEnd.emit()
    }

    ngOnDestroy(): void {
        clearTimeout(this._searchTimer)
    }

    /** Debounced so a search fires once the user pauses, not once per keystroke. */
    private _emitSearch(): void {
        if (!this.serverSide()) return

        const query = this.search()
        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => this.searchChange.emit(query), this.searchDebounce())
    }
}
